import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError, subscribeInflight, type ProductRow, type QueueCard } from "./api";
import { bustAsyncCache, useAsync } from "./useAsync";
import { OverviewSkeleton } from "./components/Skeleton";
import { ErrorBoundary, setSentryUser } from "./lib/sentry";
import { useRoute, go, type Route, type Section } from "./router";
import { BrandLockup } from "./components/KeeperMark";
import { StatusChip, productChip } from "./components/StatusChip";
import type { CardAction } from "./components/Card";
import type { QueuePlumbing } from "./components/Widgets";
import { Dashboard } from "./screens/Dashboard";
import { Attribution } from "./screens/Attribution";
import { Funnel } from "./screens/Funnel";
import { HealthReport } from "./screens/HealthReport";
import { LandingPages } from "./screens/LandingPages";
import { DemandGen } from "./screens/DemandGen";
import { History } from "./screens/History";
import { Scorecard, ScorecardPublic } from "./screens/Scorecard";
import { Integrations } from "./screens/Integrations";
import { Settings } from "./screens/Settings";
import { AcceptInvite, GoogleCallback, Login, ResetPassword, Signup, Verify } from "./screens/Auth";
import { Onboarding } from "./screens/Onboarding";
import { ConnectDone } from "./screens/ConnectDone";
import { Admin } from "./screens/admin/Admin";
import { onboardingInProgress } from "./components/connect/forms";
import { getSession, clearSession, updateSession, subscribe, isImpersonating, endImpersonation } from "./session";
import { opinlyIdentify } from "./lib/opinly";
import { identify, pageview, pathForRoute, titleForRoute } from "./lib/analytics";
import { useSyncWatch } from "./lib/syncWatch";
import { rememberedProduct, rememberProduct } from "./lib/activeProduct";
import { relativeDays, shortTime } from "./format";
import { KeeperChat, KeeperChatFab, KeeperChatTrigger, keeperChatStartsOpen } from "./components/KeeperChat";
import { ConnectLlmItem } from "./components/ConnectLlm";

export default function App() {
  const route = useRoute();
  const [session, setSessionState] = useState(getSession());
  useEffect(() => subscribe(() => setSessionState(getSession())), []);
  useEffect(() => {
    pageview(pathForRoute(route), titleForRoute(route));
  }, [route]);
  useEffect(() => {
    if (!session) setSentryUser(null);
  }, [session]);

  // Invite, reset, and Google-callback links carry their own credential; they
  // work signed in or not (completing one replaces any current session).
  if (route.name === "invite") return <AcceptInvite token={route.token} />;
  if (route.name === "reset") return <ResetPassword token={route.token} />;
  if (route.name === "google") return <GoogleCallback token={route.token} />;
  if (route.name === "scorecardShare") return <ScorecardPublic token={route.token} />;

  // Unauthenticated: only the auth screens exist.
  if (!session) {
    if (route.name === "signup") return <Signup />;
    if (route.name === "verify") return <Verify token={route.token} />;
    return <Login />;
  }
  return (
    <ErrorBoundary fallback={({ error, resetError }) => (
      <AppCrash message={error instanceof Error ? error.message : String(error)} onReset={resetError} />
    )}>
      <AuthedApp key={session.account.id} />
    </ErrorBoundary>
  );
}

function AuthedApp() {
  const route = useRoute();
  const portfolio = useAsync(() => api.portfolio(), [], { key: "portfolio" });
  const queue = useAsync(() => api.queue(), [], { key: "queue" });
  useEffect(() => {
    const account = getSession()?.account;
    if (account?.email) {
      opinlyIdentify({ email: account.email, userId: account.id });
      identify({ userId: account.id, email: account.email });
      setSentryUser({ id: account.id });
    }
    api.auth.me().then((me) => {
      updateSession({
        platform_admin: me.platform_admin,
        name: me.name,
        account_name: me.account_name,
        email: me.email,
        role: me.role,
        member_role: me.member_role,
      });
    }).catch(() => {});
  }, []);

  // Resolutions are written straight to the API; the local copy is patched so
  // the card collapses to its receipt without a round trip. The API is the
  // record of truth — a reload always shows the server's version.
  const [patches, setPatches] = useState<Record<string, Partial<QueueCard>>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [chatOpen, setChatOpen] = useState(keeperChatStartsOpen);

  const products = useMemo(() => portfolio.data ?? [], [portfolio.data]);
  const onProductRoute = route.name === "product";
  // Remember the last product the tenant was viewing so System pages
  // (Integrations/Team/Settings/…) don't silently flip the sidebar back to
  // products[0], and so a slugless route (#/) resumes where they left off.
  // Persisted, not a ref: connecting a source hands the browser to Google and
  // saving credentials reloads the page, and both used to lose the product.
  const remembered = rememberedProduct();
  const [systemSlug, setSystemSlug] = useState<string | null>(null);
  const product = onProductRoute
    ? (route.slug
        ? products.find((p) => p.slug === route.slug)
        : products.find((p) => p.slug === remembered) ?? products[0]) ?? null
    : null;
  useEffect(() => {
    if (product) {
      rememberProduct(product.slug);
      setSystemSlug(product.slug);
    }
  }, [product?.slug]);
  useEffect(() => {
    if (route.name === "login" || route.name === "signup" || route.name === "verify") {
      go({ name: "product", slug: null, section: "dashboard" });
    }
  }, [route.name]);
  // The sidebar keeps naming the product even on Integrations/Pipeline, so the
  // section links always know where they lead back to.
  const sidebarProduct = product
    ?? products.find((p) => p.slug === (systemSlug ?? remembered))
    ?? products[0]
    ?? null;

  const cards = useMemo(
    () => (queue.data ?? []).map((c) => ({ ...c, ...patches[c.id] })),
    [queue.data, patches]
  );
  const scopedCards = product ? cards.filter((c) => c.product_slug === product.slug) : cards;
  const pendingCount = scopedCards.filter((c) => c.status === "pending").length;

  const actor = getSession()?.account.email ?? "you";

  const onAct = useCallback(
    async (id: string, action: CardAction) => {
      if (action === "details" || action === "log" || action === "undo") return;
      if (action === "view") {
        const target = cards.find((c) => c.id === id)?.product_slug;
        if (target) go({ name: "product", slug: target, section: "dashboard" });
        return;
      }

      const patch = (p: Partial<QueueCard>) =>
        setPatches((prev) => ({ ...prev, [id]: { ...prev[id], ...p } }));

      setBusyId(id);
      setErrors((e) => {
        const { [id]: _drop, ...rest } = e;
        return rest;
      });
      try {
        const now = new Date().toISOString();
        if (action === "approve") {
          await api.approve(id);
          patch({ status: "approved", resolved_by: actor, resolved_at: now });
        } else if (action === "reject") {
          await api.reject(id);
          patch({ status: "rejected", resolved_by: actor, resolved_at: now });
        } else if (action === "snooze") {
          await api.snooze(id, 24);
          patch({ status: "snoozed", snoozed_until: new Date(Date.now() + 86_400_000).toISOString() });
        } else if (action === "restore") {
          await api.restore(id);
          patch({ status: "pending", snoozed_until: null, resolved_by: null, resolved_at: null });
        }
        portfolio.reload();
      } catch (e) {
        setErrors((prev) => ({ ...prev, [id]: e instanceof ApiError ? e.message : String(e) }));
        // A 403 on approve means the policy engine refused and the API has
        // already rejected the card. Reflect that; don't leave it looking pending.
        if (e instanceof ApiError && e.status === 403) {
          patch({ status: "rejected", resolved_by: "policy", resolved_at: new Date().toISOString() });
        }
      } finally {
        setBusyId(null);
      }
    },
    [cards, portfolio, actor]
  );

  const queuePlumbing: QueuePlumbing = { cards: scopedCards, errors, busyId, onAct };

  return (
    <div className={`shell${chatOpen ? " chat-open" : ""}`}>
      <Sidebar
        route={route}
        product={sidebarProduct}
        products={products}
        pendingCount={pendingCount}
        onPickSystemProduct={(slug) => {
          rememberProduct(slug);
          setSystemSlug(slug);
        }}
      />
      <div className="main">
        <ImpersonationBanner />
        <TrialBanner />
        <SyncBanner />
        <Topbar
          route={route}
          product={product ?? sidebarProduct}
          pendingCount={pendingCount}
          onAskKeeper={() => setChatOpen(true)}
          onReload={() => {
            setPatches({});
            setErrors({});
            bustAsyncCache();
            portfolio.reload();
            queue.reload();
          }}
        />

        {portfolio.error ? (
          <Fatal message={portfolio.error} />
        ) : portfolio.loading ? (
          <OverviewSkeleton />
        ) : route.name === "connected" ? (
          <ConnectDone state={route.state} />
        ) : route.name === "admin" ? (
          <Admin tab={route.tab} id={route.id} />
        ) : route.name === "newProduct" ? (
          <Onboarding startAt="manual" onCreated={() => { portfolio.reload(); queue.reload(); }} />
        ) : (products.length === 0 || (onboardingInProgress() && route.name === "product")) ? (
          <Onboarding onCreated={() => { portfolio.reload(); queue.reload(); }} />
        ) : route.name === "integrations" ? (
          <Integrations product={sidebarProduct} />
        ) : route.name === "settings" ? (
          <Settings tab={route.tab} />
        ) : product ? (
          <ProductSection section={route.name === "product" ? route.section : "dashboard"}
                          product={product} queue={queuePlumbing} />
        ) : (
          <Splash
            text={
              onProductRoute && route.slug
                ? `No project with slug "${route.slug}".`
                : "Loading…"
            }
          />
        )}
      </div>
      <KeeperChat
        open={chatOpen}
        onOpenChange={setChatOpen}
        product={sidebarProduct}
        route={route}
      />
      {!chatOpen && <KeeperChatFab onClick={() => setChatOpen(true)} />}
    </div>
  );
}

function ProductSection({ section, product, queue }: { section: Section; product: ProductRow; queue: QueuePlumbing }) {
  switch (section) {
    case "dashboard": return <Dashboard product={product} queue={queue} />;
    case "attribution": return <Attribution product={product} />;
    case "funnel": return <Funnel product={product} />;
    case "pages": return <LandingPages product={product} />;
    case "demand": return <DemandGen product={product} />;
    case "scorecard": return <Scorecard product={product} />;
    case "health": return <HealthReport product={product} />;
    case "history": return <History product={product} />;
  }
}

/** The product's sections — the redesigned left panel. */
const SECTIONS: Array<{ id: Section; label: string; badge?: boolean }> = [
  { id: "dashboard", label: "Dashboard", badge: true },
  { id: "attribution", label: "Attribution" },
  { id: "funnel", label: "Funnel" },
  { id: "pages", label: "Pages" },
  { id: "demand", label: "Demand" },
  { id: "scorecard", label: "Scorecard" },
  { id: "health", label: "Health" },
  { id: "history", label: "History" },
];

/** Portfolio-wide pages, kept out of the product's own section list. */
function Sidebar({
  route,
  product,
  products,
  pendingCount,
  onPickSystemProduct,
}: {
  route: Route;
  product: ProductRow | null;
  products: ProductRow[];
  pendingCount: number;
  onPickSystemProduct: (slug: string) => void;
}) {
  const activeSection = route.name === "product" ? route.section : null;
  const session = getSession();
  const isAdmin = !!session?.account.platform_admin;

  return (
    <aside className="sidebar">
      <div className="brand">
        <BrandLockup />
      </div>

      <div className="switch-field">
        <div className="eyebrow">Project</div>
        <ProductSwitcher
          product={product}
          products={products}
          section={activeSection ?? "dashboard"}
          route={route}
          onPickSystemProduct={onPickSystemProduct}
        />
      </div>

      <nav className="nav">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className="nav-item"
            disabled={products.length === 0}
            aria-current={activeSection === s.id}
            onClick={() => go({ name: "product", slug: product?.slug ?? null, section: s.id })}
          >
            <span style={{ flex: 1, minWidth: 0 }}>{s.label}</span>
            {s.badge && pendingCount > 0 && <span className="nav-badge num">{pendingCount}</span>}
          </button>
        ))}
      </nav>

      <div className="eyebrow" style={{ padding: "18px 8px 8px" }}>
        System
      </div>
      <nav className="nav">
        <button
          type="button"
          className="nav-item"
          aria-current={route.name === "integrations"}
          onClick={() => go({ name: "integrations" })}
        >
          <span style={{ flex: 1, minWidth: 0 }}>Integrations</span>
        </button>
        <button
          type="button"
          className="nav-item"
          aria-current={route.name === "settings"}
          onClick={() => go({ name: "settings" })}
        >
          <span style={{ flex: 1, minWidth: 0 }}>Settings</span>
        </button>
        <ConnectLlmItem />
        {isAdmin && (
          <button
            type="button"
            className="nav-item"
            aria-current={route.name === "admin"}
            onClick={() => go({ name: "admin", tab: "overview" })}
          >
            <span style={{ flex: 1, minWidth: 0 }}>Admin</span>
          </button>
        )}
      </nav>

      <div style={{ flex: 1 }} />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "14px 10px 0",
          borderTop: "1px solid var(--border-hairline)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--pos)" }} />
          <span style={{ fontSize: 12.5, color: "var(--text-2)" }}>Keeper watching</span>
        </div>
        <AccountFooter />
      </div>
    </aside>
  );
}

/**
 * Says what a running sync is doing, on every page.
 *
 * It sits above the content rather than inside any one screen because the
 * tenant who most needs it is the one who just connected a source and went
 * looking at the Dashboard or the Funnel to see whether it worked. The numbers
 * under it refresh on their own while this is up (useSyncWatch), so it doubles
 * as the explanation for why they keep changing.
 */
function ImpersonationBanner() {
  const [busy, setBusy] = useState(false);
  if (!isImpersonating()) return null;
  const session = getSession();
  const name = session?.account.account_name || session?.account.email || "workspace";
  const exit = async () => {
    setBusy(true);
    try {
      await api.admin.endImpersonation();
    } catch {
      /* token may already be dead; restore the stashed admin session anyway */
    }
    endImpersonation();
    bustAsyncCache();
    go({ name: "admin", tab: "overview" });
  };
  return (
    <div className="impersonation-banner" role="status">
      <span>Viewing {name} read-only as support</span>
      <button type="button" className="btn-text" onClick={exit} disabled={busy}>
        Exit
      </button>
    </div>
  );
}

function SyncBanner() {
  const { phase } = useSyncWatch();
  if (!phase) return null;
  return (
    <div className="sync-banner" role="status" aria-live="polite">
      <span className="sync-banner-spinner" aria-hidden />
      <span className="sync-banner-text">{phase}</span>
      <span className="sync-banner-note">
        Figures fill in as history arrives — you can keep working.
      </span>
    </div>
  );
}

function TrialBanner() {
  const billing = useAsync(() => api.billing.get(), []);
  const b = billing.data;
  if (!b) return null;
  if (b.plan_status === "trialing" && b.trial_days_left !== null && b.trial_days_left <= 7) {
    const days = b.trial_days_left;
    const urgent = days <= 3;
    const dayLabel = days === 1 ? "1 day" : `${days} days`;
    return (
      <div
        className={`trial-banner ${urgent ? "trial-banner-urgent" : "trial-banner-active"}`}
        role="status"
      >
        <div className="trial-banner-content">
          <span className="trial-banner-pill">{days === 0 ? "Ends today" : `${dayLabel} left`}</span>
          <span className="trial-banner-text">
            {urgent
              ? (days === 0
                  ? "Growth trial ends today — landing analyses, CSV export and the digest pause on Free."
                  : `Growth trial ends in ${dayLabel} — landing analyses, CSV export and the digest pause on Free.`)
              : `Growth trial — ${dayLabel} left. Add billing to keep AI wire-up, advanced analyses, and unlimited projects.`}
          </span>
        </div>
        <button
          type="button"
          className="trial-banner-btn"
          onClick={() => go({ name: "settings", tab: "billing" })}
        >
          Add billing
        </button>
      </div>
    );
  }
  if (b.plan === "free" && b.plan_status === "canceled") {
    return (
      <div className="trial-banner trial-banner-ended" role="status">
        <div className="trial-banner-content">
          <span className="trial-banner-pill ended">Free</span>
          <span className="trial-banner-text">
            Growth trial ended — you're on Free. Upgrade to reactivate AI features and multi-project tracking.
          </span>
        </div>
        <button
          type="button"
          className="trial-banner-btn"
          onClick={() => go({ name: "settings", tab: "billing" })}
        >
          Upgrade
        </button>
      </div>
    );
  }
  return null;
}

function ProductSwitcher({
  product,
  products,
  section,
  route,
  onPickSystemProduct,
}: {
  product: ProductRow | null;
  products: ProductRow[];
  section: Section;
  route: Route;
  onPickSystemProduct: (slug: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="account-switch" ref={wrap}>
      <button
        type="button"
        className="account-switch-btn"
        aria-label="Project"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="account-switch-label">{product?.name ?? "No projects"}</span>
        <span className="account-switch-caret" aria-hidden>▾</span>
      </button>
      {open && (
        <div className="account-switch-menu" role="listbox">
          {products.map((p) => (
            <button
              key={p.product_id}
              type="button"
              className="account-switch-item"
              role="option"
              aria-selected={p.slug === product?.slug}
              onClick={() => {
                setOpen(false);
                if (route.name === "integrations") {
                  onPickSystemProduct(p.slug);
                  go({ name: "integrations" });
                  return;
                }
                go({ name: "product", slug: p.slug, section });
              }}
            >
              {p.name}
            </button>
          ))}
          {/* Creating goes through the onboarding wizard so a product added
              later gets the same create → connect → track walk as the first. */}
          <button
            type="button"
            className="account-switch-item account-switch-new"
            onClick={() => {
              setOpen(false);
              go({ name: "newProduct" });
            }}
          >
            New project…
          </button>
        </div>
      )}
    </div>
  );
}

function AccountFooter() {
  const session = getSession();
  if (!session) return null;
  const signOut = () => {
    api.auth.logout().catch(() => {});
    clearSession();
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <span
        style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: "var(--text-faint)",
                 overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={session.account.email}
      >
        {session.account.email}
        {session.account.role === "operator" && (
          <span style={{ color: "var(--keeper)", marginLeft: 6, textTransform: "uppercase", fontSize: 9.5, letterSpacing: "0.07em" }}>
            operator
          </span>
        )}
      </span>
      <button type="button" className="btn-link" onClick={signOut}>Sign out</button>
    </div>
  );
}

const SECTION_TITLES: Record<Section, string> = {
  dashboard: "Dashboard",
  attribution: "Attribution",
  funnel: "Funnel",
  pages: "Pages",
  demand: "Demand",
  scorecard: "Scorecard",
  health: "Health",
  history: "History",
};

function Topbar({
  route,
  product,
  pendingCount,
  onAskKeeper,
  onReload,
}: {
  route: Route;
  product: ProductRow | null;
  pendingCount: number;
  onAskKeeper: () => void;
  onReload: () => void;
}) {
  const onProduct = route.name === "product";
  const title =
    route.name === "integrations"
      ? (product ? `${product.name} · Integrations` : "Integrations")
      : route.name === "settings"
        ? (route.tab && route.tab !== "general"
            ? `Settings · ${route.tab[0].toUpperCase() + route.tab.slice(1)}`
            : "Settings")
        : route.name === "admin"
          ? "Admin"
          : product
            ? `${product.name} · ${SECTION_TITLES[route.name === "product" ? route.section : "dashboard"]}`
            : "FunnelKeeper";

  // Products are stored in their own currency and the views do not convert, so
  // the header states what is actually on screen rather than claiming USD.
  return (
    <div className="topbar">
      <span style={{ fontSize: 14, fontWeight: 500 }}>{title}</span>
      {onProduct && product && (
        <StatusChip
          kind={productChip(product.stage, product.gate_result)}
          label={gateLabel(product)}
        />
      )}
      <span style={{ flex: 1 }} />
      <span className="num" style={{ fontSize: 12.5, color: "var(--text-faint)" }}>
        {onProduct && product ? `${product.currency} · ` : ""}
        {pendingCount} pending
      </span>
      <KeeperChatTrigger onClick={onAskKeeper} />
      <button type="button" className="btn-text" onClick={onReload} title="Refetch from the API">
        Refresh
      </button>
      <RequestProgress />
    </div>
  );
}

function RequestProgress() {
  const [n, setN] = useState(0);
  useEffect(() => subscribeInflight(setN), []);
  if (n === 0) return null;
  return (
    <div className="progress-bar" role="progressbar" aria-label="Loading" aria-busy="true">
      <div className="progress-bar-indeterminate" />
    </div>
  );
}

function gateLabel(p: ProductRow): string | undefined {
  if (p.gate_result === "pass") return "Passing · gate cleared";
  if (p.gate_result === "fail") return "Failing · gate missed";
  if (p.gate_result === "pivot") return "Pivot";
  const rel = relativeDays(p.gate_due_date ?? p.gate_date);
  return rel ? `Pre-gate · ${rel}` : undefined;
}

function Splash({ text }: { text: string }) {
  return (
    <div className="page" style={{ padding: "60px 40px", color: "var(--text-4)" }}>
      {text}
    </div>
  );
}

function Fatal({ message }: { message: string }) {
  return (
    <div className="page" style={{ paddingTop: 40 }}>
      <div className="panel" style={{ borderColor: "var(--neg)" }}>
        <h2 style={{ color: "var(--neg)" }}>The API is unreachable</h2>
        <p style={{ fontSize: 13.5, color: "var(--text-3)", lineHeight: 1.6 }}>{message}</p>
        <p style={{ fontSize: 13, color: "var(--text-4)", lineHeight: 1.6 }}>
          Check <code>VITE_API_URL</code> and that this origin is listed in the API's{" "}
          <code>DASHBOARD_ORIGINS</code>. Last attempt {shortTime(new Date().toISOString())}.
        </p>
      </div>
    </div>
  );
}

function AppCrash({ message, onReset }: { message: string; onReset?: () => void }) {
  return (
    <div className="page" style={{ paddingTop: 40 }}>
      <div className="panel" style={{ borderColor: "var(--neg)" }}>
        <h2 style={{ color: "var(--neg)" }}>Something went wrong</h2>
        <p style={{ fontSize: 13.5, color: "var(--text-3)", lineHeight: 1.6 }}>{message}</p>
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            className="btn-solid"
            onClick={() => {
              if (onReset) onReset();
              else location.reload();
            }}
          >
            Reload app
          </button>
        </div>
      </div>
    </div>
  );
}
